import {
  AnyContent,
  ARTICLE_TYPE,
  Feed,
  FEED_TYPE_KEYWORD,
  FEED_TYPE_THREADS,
  GIF_TYPE,
  LegacyArticle,
  LegacyFeed,
  LegacyFolder,
  LegacyGif,
  LegacyPhoto,
  LegacyVideo,
  Photo,
  PHOTO_TYPE,
  RangeFilter,
  Stream,
  TEXT_TYPE,
  VIDEO_TYPE
} from 'interfaces'
import { STOCK_ALL_FILTER } from 'interfaces/Content/StockContentTypes'
import { Observable, zip } from 'rxjs'
import { map } from 'rxjs/operators'
import { gifAdapter } from 'services/content/adapters/gifs'
import { textAdapter } from 'services/content/adapters/texts'
import {
  articleAdapter,
  feedAdapter,
  photoAdapter,
  STOCK_CONTENT_PROVIDER_PARAMS_MAP,
  streamAdapter,
  videoAdapter
} from 'services/content/util'
import { getState, StoreThunkAction, StoreThunkDispatch } from 'store/state'
import { CONTENT_TYPE_STOCK_MAP } from 'utils/content'
import { createMixedArrayFromObject } from 'utils/sort/mixedArray'
import { V1 } from './net'
import { FilterType } from 'shared'

export * from './latest/actions'

export interface SourcesSearchSuccessResponse {
  feeds: Record<string, Feed[]>,
  streams: Stream[],
  count: number,
  needle: string,
  scopes: string[],
  page: number
}

export function search(
  needle: string,
  scopes: string[],
  page: number,
  pageSize: number,
  feedId?: string,
  streamId?: string
): StoreThunkAction<Observable<SourcesSearchSuccessResponse>> {
  return (dispatch: StoreThunkDispatch, _getState: getState) => {
    return dispatch(V1.search(needle, scopes, page, pageSize, feedId, streamId)).pipe(
      map(data => {
        let sourceByIdFormatted: Feed | Stream
        const filtersByNetwork = data.filters
        const sourceById = data.byId
        const feedsCount = Object.values(data.feeds).reduce((count: number, items: any[]) => {
          count += items.length
          return count
        }, 0)

        const feeds: { [key: string]: Feed[] } = scopes.reduce((results: { [key: string]: any[] }, scope) => {
          results[scope] = (data.feeds[scope] || []).map(f => {
            const feed = feedAdapter(f)
            feed.titleHTML = (f as any).highlightResult?.name?.value
            feed.sources = filtersByNetwork[feed.type]
            return feed
          })
          if (scope === FEED_TYPE_KEYWORD) {
            // Move exact match to first position
            const exactMatchIndex = results[scope].findIndex(f => f.name.toLowerCase() === needle.toLowerCase())
            if (exactMatchIndex !== -1) {
              const item = results[scope].splice(exactMatchIndex, 1)[0]
              results[scope].unshift(item)
            }
          }
          return results
        }, {})

        const streams = (data.streams || []).map(s => {
          const stream = streamAdapter(s)
          stream.titleHTML = (s as any).highlightResult?.name?.value
          return stream
        })
        let count = feedsCount + streams.length

        // NOTE: If we've requested a source by id, it will be included in the response,
        // and possibly - in the results for the requested page
        if (sourceById) {
          if (feedId) {
            sourceByIdFormatted = feedAdapter(sourceById as LegacyFeed)
            sourceByIdFormatted.sources = filtersByNetwork[sourceByIdFormatted.type]
            const pageFeeds = feeds[(sourceByIdFormatted as Feed).type]

            if (pageFeeds.find(f => f.id === sourceByIdFormatted.id) === undefined) {
              pageFeeds.push(sourceByIdFormatted)
            }
          }

          if (streamId) {
            sourceByIdFormatted = streamAdapter(sourceById as LegacyFolder)
            if (streams.find(s => s.id === sourceByIdFormatted.id) === undefined) {
              streams.push(sourceByIdFormatted)
            }
          }
          count += 1
        }

        return { feeds, streams, count, needle, scopes, page }
      }))
  }
}

export function fbLiveSearch(needle: string): StoreThunkAction<Observable<any>> {
  return (dispatch: StoreThunkDispatch) => {
    return dispatch(V1.fbLiveSearch(needle)).pipe(
      map(response => response.feeds.facebook.map(feedAdapter).map(f => ({
        ...f,
        live: true,
        handle: f.handle.toLowerCase().replace(/\/$/, '') // remove trailing slash
      }))))
  }
}

export function twitterLiveSearch(needle: string): StoreThunkAction<Observable<any>> {
  return (dispatch: StoreThunkDispatch) => {
    return dispatch(V1.twitterLiveSearch(needle)).pipe(
      map(response => response.feeds.twitter.map(feedAdapter).map(f => ({ ...f, live: true }))))
  }
}

export function blueskyUsersSearch(needle: string, ownerId: string): StoreThunkAction<Observable<any>> {
  return (dispatch: StoreThunkDispatch) => {
    return dispatch(V1.blueskyUsersSearch(needle, ownerId)).pipe(
      map(response => {
        return response.feeds.bluesky.actors.map((item: any) => {
          return {
            id: item.did,
            uniqueSource: '',
            handle: item.handle,
            type: 'bluesky' as any,
            isFeed: true,
            order: 0,
            name: item.displayName,
            image: item.avatar,
            sources: []
          }
        })
      }))
  }
}

export function linkedInLiveSearch(needle: string): StoreThunkAction<Observable<any>> {
  return (dispatch: StoreThunkDispatch) => {
    const rez = dispatch(V1.linkedInLiveSearch(needle))
    return rez.pipe(map(response => response.feeds.linkedin.map(feedAdapter).map(f => ({ ...f, live: true }))))
  }
}

export function redditLiveSearch(needle: string): StoreThunkAction<Observable<any>> {
  return (dispatch: StoreThunkDispatch) => {
    return dispatch(V1.redditLiveSearch(needle)).pipe(
      map(response => response.feeds.reddit.map(feedAdapter).map(f => ({ ...f, live: true }))))
  }
}

export function blueskyLiveSearch(needle: string): StoreThunkAction<Observable<any>> {
  return (dispatch: StoreThunkDispatch) => {
    return dispatch(V1.blueskyLiveSearch(needle))
      .pipe(map(response => response.feeds.bluesky.map(feedAdapter).map(f => ({ ...f, live: true }))))
  }
}

export function searchStockContent(query: string, type: FilterType, provider: string = '', sortBy?: string) {
  return (dispatch: StoreThunkDispatch) => {
    if (type === STOCK_ALL_FILTER) {
      const photos$ = dispatch(V1.searchStockContent(query, PHOTO_TYPE))
      const videos$ = dispatch(V1.searchStockContent(query, VIDEO_TYPE))
      const gifs$ = dispatch(V1.searchStockContent(query, GIF_TYPE))
      return zip(photos$, videos$, gifs$).pipe(
        map(([photosRes, videosRes, gifsRes]) => {
          const photosFormatted = photosRes.content.map((p: LegacyPhoto) => photoAdapter(p, true))
          const videosFormatted = videosRes.content.map((v: LegacyVideo) => videoAdapter(v, true))
          const gifsFormatted = gifsRes.content.map((g: LegacyGif) => gifAdapter(g, true))
          const photosByProvider = photosFormatted.reduce((map: Record<string, any[]>, item: any) => {
            if (!map[item.provider]) {
              map[item.provider] = []
            }
            map[item.provider].push(item)
            return map
          }, {})
          const mixedPhotos = createMixedArrayFromObject<Photo>(photosByProvider)
          const photosCount = mixedPhotos.length
          const videosCount = videosFormatted.length
          const gifsCount = gifsFormatted.length
          const maxLength = Math.max(photosCount, videosCount, gifsCount)
          const allContent: AnyContent[] = []
          for (let i = 0; i < maxLength; i++) {
            if (i < photosCount) {
              allContent.push(mixedPhotos[i])
            }
            if (i < videosCount) {
              allContent.push(videosFormatted[i])
            }
            if (i < gifsCount) {
              allContent.push(gifsFormatted[i])
            }
          }

          return allContent
        }))
    }

    const filter = CONTENT_TYPE_STOCK_MAP[type]
    const providerWithType = STOCK_CONTENT_PROVIDER_PARAMS_MAP[filter]
      ? STOCK_CONTENT_PROVIDER_PARAMS_MAP[filter][provider] || provider
      : provider
    return dispatch(V1.searchStockContent(query, filter, providerWithType, sortBy)).pipe(map(response => {
      if (filter === PHOTO_TYPE) {
        const photos = response.content.map((p: LegacyPhoto) => photoAdapter(p, true))
        const grouped = photos.reduce((map: Record<string, any[]>, item: any) => {
          if (!map[item.provider]) {
            map[item.provider] = []
          }
          map[item.provider].push(item)
          return map
        }, {})
        return createMixedArrayFromObject<Photo>(grouped)
      } if (filter === VIDEO_TYPE) {
        return response.content.map((v: LegacyVideo) => videoAdapter(v, true))
      } if (filter === ARTICLE_TYPE) {
        return response.content.map((v: LegacyArticle) => articleAdapter(v))
      } if (filter === GIF_TYPE) {
        return response.content.map((g: LegacyGif) => gifAdapter(g, true))
      }
      return []
    }))
  }
}

const THREADS_AUTHOR_REGEX = /@[\w.]+/

// Threads response items carry no feed; the author handle lives in the post's social link.
function threadsAuthorHandle(socialLink: string): string {
  const match = (socialLink || '').match(THREADS_AUTHOR_REGEX)
  return match ? match[0] : ''
}

export function searchThreadsContent(query: string, contentType: string, rangeFilter: RangeFilter) {
  return (dispatch: StoreThunkDispatch) => {
    return dispatch(V1.searchThreadsContent(query, contentType, rangeFilter)).pipe(map((response: { content: any[] }) => {
      return response.content
        .map((item: any) => {
          let adapted: AnyContent | null = null
          switch (contentType) {
            case PHOTO_TYPE:
              adapted = photoAdapter(item)
              break
            case VIDEO_TYPE:
              adapted = videoAdapter(item)
              break
            case ARTICLE_TYPE:
              adapted = articleAdapter(item)
              break
            case TEXT_TYPE:
              adapted = textAdapter(item)
              break
            default:
              return null
          }
          const handle = threadsAuthorHandle(item.socialLink)
          adapted.feed = {
            ...adapted.feed,
            type: FEED_TYPE_THREADS,
            name: handle,
            handle,
            uniqueSource: adapted.feed.uniqueSource || handle
          }
          return adapted
        })
        .filter(Boolean)
    }))
  }
}
